import PickersDay from '@mui/lab/PickersDay'
import * as ort from 'onnxruntime-web';
import { DateTime } from "luxon"

import {
  formatTime,
  isAllDay,
  removeTimezoneFromISO
} from './FormatDate'

import {
  styled,
  Switch,
  Box,
} from '@mui/material'

import {
  AirConditioner,
  Blackboard,
  Plug,
  Projector,
  VideoConference,
  Wifi,
  Monitor
} from '../components/Svgs/Amenities'

import { InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material';
import { Trans } from 'react-i18next';
import { CONFIDENCE_THRESHOLD, EIGHT_AM, FIVE_PM, IOU_THRESHOLD, MIN_AREA_RATIO, MIN_ASPECT_RATIO, MODEL_INPUT_SHAPE, SHARPNESS_THRESHOLD } from '../../variables';

const getCurrentBrowser = () => {
  var ua = navigator.userAgent
  var tem
  var M = ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || []
  if (/trident/i.test(M[1])) {
    tem = /\brv[ :]+(\d+)/g.exec(ua) || []
    return 'IE ' + (tem[1] || '')
  }
  if (M[1] === 'Chrome') {
    tem = ua.match(/\b(OPR|Edge)\/(\d+)/);
    if (tem != null) return tem.slice(1).join(' ').replace('OPR', 'Opera')
  }
  M = M[2] ? [M[1], M[2]] : [navigator.appName, navigator.appVersion, '-?']
  if ((tem = ua.match(/version\/(\d+)/i)) != null) M.splice(1, 1, tem[1])
  return M.join(' ');
}

const getPlace = item => {
  const {
    site = { name: '' },
    building = { name: '' },
    floor = { name: '' }
  } = item
  return `${site.name}, ${building.name}, ${floor.name}`
}

const getTime = (item, t) => {
  let {
    entry_time = '',
    exit_time = ''
  } = item

  entry_time = entry_time ? removeTimezoneFromISO(entry_time) : ''
  exit_time = exit_time ? removeTimezoneFromISO(exit_time) : ''

  if (isAllDay({ entry_time, exit_time })) return t('all day')
  return `${formatTime(entry_time)} - ${formatTime(exit_time)}`
}

const sortItems = (items = []) => {
  return items.sort((a, b) => {
    const _a = a.name.toLowerCase()
    const _b = b.name.toLowerCase()

    if (_a < _b) return -1
    else if (_a > _b) return 1
    return 0
  })
}

const getFullName = params => {
  const {
    first_name,
    last_name_one
  } = params

  return `${first_name} ${last_name_one}`
}

const getNormalizedEmailList = (value = '') => (
  value
    .split(',')
    .map(item => item.trim().toLowerCase())
    .filter(Boolean)
    .join(',')
)

const isValidEmailList = (value = '') => {
  if (!value) return false
  const emails = getNormalizedEmailList(value).split(',').filter(Boolean)
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emails.length > 0 && emails.every(email => regex.test(email))
}

const getFullNameGuest = (params = {}) => {
  const {
    first_name = '',
    last_name_one = '',
    last_name_two = '',
  } = params

  return `${first_name} ${last_name_one} ${last_name_two}`.trim()
}

const getWorkplaceReasons = t => [
  t('private meeting'),
  t('workday'),
  t('training'),
  t('special event'),
  t('others'),
]

const getNoRowOverlay = () => {
  const StyledGridOverlay = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    textAlign: 'center',
    fontSize: '18px'
  }))

  return (
    <StyledGridOverlay>
      <InfoOutlinedIcon fontSize="large" />
      <Box sx={{ mt: 1, color: '#181818', fontWeight: 300 }}>
        <Trans i18nKey="No Rows">
          Oops, you still have no existing records. <br />Create a new registration and enjoy the benefits that Lobby has for you.
        </Trans>
      </Box>
    </StyledGridOverlay>
  )
}

const getNoRowOverlayInvitations = () => {
  const StyledGridOverlay = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    textAlign: 'center',
    fontSize: '18px'
  }))

  return (
    <StyledGridOverlay>
      <InfoOutlinedIcon fontSize="large" />
      <Box sx={{ mt: 1, width: '350px', fontWeight: '400' }}>
        <Trans i18nKey="No pre-registered visitors for the selected date range">
          Oops, there are no pre registers available for the selected date range. <strong>Select another date range to </strong> consult.
        </Trans>
      </Box>
    </StyledGridOverlay>
  )
}

const getNoResultsOverlayInvitations = () => {
  const StyledGridOverlay = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    textAlign: 'center',
    fontSize: '18px'
  }))

  return (
    <StyledGridOverlay>
      <InfoOutlinedIcon fontSize="large" />
      <Box sx={{ mt: 1, width: '350px', fontWeight: '400' }}>
        <Trans i18nKey="No data found">
          Oops, there is no data available according to your search. <strong>Select other parameters</strong> to search.
        </Trans>
      </Box>
    </StyledGridOverlay>
  )
}

const getQuotedContent = value => `"${String(value ?? '').replace(/"/g, '\\"')}"`

const ParsoSwitch = styled(Switch)(({ theme, checked, leftlabel, rightlabel }) => ({
  padding: 0,
  width: 235,
  height: 40,
  fontSize: 13,
  '& .MuiSwitch-switchBase': {
    padding: 2
  },
  '& .MuiSwitch-track': {
    borderRadius: 20,
    backgroundColor: '#EFEFEF',
    '&:before, &:after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      width: 113,
      height: 32,
    },
    '&:before': {
      content: getQuotedContent(leftlabel),
      left: 12,
      marginTop: 10
    },
    '&:after': {
      content: getQuotedContent(rightlabel),
      right: 12,
      marginTop: 10
    }
  },
  '& .Mui-checked+.MuiSwitch-track': {
    backgroundColor: '#EFEFEF'
  },
  '& .Mui-checked': {
    transform: 'translateX(115px)'
  },
  '& .MuiSwitch-thumb': {
    padding: '10px 0 0 0',
    boxSizing: 'border-box',
    boxShadow: 'none',
    borderRadius: 20,
    background: 'white',
    width: 113,
    height: 32,
    margin: 2,
    '&:before, &:after': {
      content: '""',
      transform: 'translateY(-50%)',
      width: 113,
      height: 32,
      color: 'black'
    },
    '&:before': {
      content: getQuotedContent(checked ? rightlabel : leftlabel),
    },
  },
}));


const debounce = (func, wait, immediate) => {
  let timeout;

  const debounced = function () {
    const context = this;
    const args = arguments;

    const later = function () {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };

    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };

  debounced.cancel = function () {
    clearTimeout(timeout);
  };

  return debounced;
};

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) => prop !== 'selected'
})(({ theme, selected, unavailable }) => ({
  margin: '0 2px',
  borderTopLeftRadius: '50%',
  borderBottomLeftRadius: '50%',
  borderTopRightRadius: '50%',
  borderBottomRightRadius: '50%',
  ...(selected && {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    '&:hover, &:focus': {
      backgroundColor: theme.palette.primary.dark
    }
  }),
  ...(unavailable && {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
    '&:hover, &:focus': {
      backgroundColor: theme.palette.error.dark
    }
  })
}));

const stopEvent = e => {
  if (e.preventDefault !== undefined) e.preventDefault()
  if (e.stopPropagation !== undefined) e.stopPropagation()
}

const getNumberToWeekDay = (n, t) => {
  if (n === 1) return t('Monday')
  if (n === 2) return t('Tuesday')
  if (n === 3) return t('Wednesday')
  if (n === 4) return t('Thursday')
  if (n === 5) return t('Friday')
  if (n === 6) return t('Saturday')
  if (n === 7) return t('Sunday')
  return ''
}

const getAmenitieImage = i => {
  if (i === 0) return <AirConditioner />
  if (i === 1) return <Blackboard />
  if (i === 2) return <Plug />
  if (i === 3) return <Projector />
  if (i === 4) return <VideoConference />
  if (i === 5) return <Wifi />
  return <Monitor />
}

const getVisitDestination = (destinations = []) => {
  const seen = {}
  const unique = destinations.filter(c => {
    if (!c || c.id === undefined) return false
    if (seen[c.id]) return false
    seen[c.id] = true
    return true
  })

  return unique.sort((a, b) => {
    const _a = (a.name || '').toLowerCase()
    const _b = (b.name || '').toLowerCase()
    if (_a < _b) return -1
    if (_a > _b) return 1
    return 0
  })
}

const getCompany = (company_id, companies = []) => {
  if (!company_id || companies.length === 0) return ''
  return companies.find(c => c.id === company_id)?.name || ''
}

const isHome = () => window.location.pathname === '/'

const defaultFunction = () => { }

const downloadQRAsImage = (visitorName, date, onDownloadComplete = () => { }, options = {}) => {
  const element = document.getElementById('qr-print-area')
  if (!element) return
  import('html2canvas').then(({ default: html2canvas }) => {
    import('file-saver').then(({ saveAs }) => {
      html2canvas(element, {
        backgroundColor: '#FFFFFF',
        onclone: documentClone => {
          const card = documentClone.getElementById('qr-print-area')
          if (card) {
            card.querySelector('[data-qr-reception-notes]')?.remove()
            card.style.background = '#FFFFFF'
            card.style.boxShadow = 'none'
            const qr = card.querySelector('.qr-wrapper')
            if (qr) qr.style.boxShadow = 'none'
            const info = card.querySelector('.qr-info-wrapper')
            if (info) {
              info.style.background = '#F9FAFB'
              info.style.borderRadius = '10px'
              info.style.padding = '24px'
            }
          }
          if (!options.infoMessageForDownload) return
          const messageNode = card?.querySelector('[data-qr-download-message]')
          if (messageNode) messageNode.textContent = options.infoMessageForDownload
        },
      }).then(canvas => {
        canvas.toBlob(blob => {
          if (!blob) return
          saveAs(blob, `qr-${visitorName}-${date}.jpg`)
          onDownloadComplete()
        })
      })
    })
  })
}

const getAccessControlQR = async (invitationID, api, { defaultSuccess, defaultCatch, navigate, setAccessControlQR }) => {
  try {
    const response = await api.get(`/invitation/qr/${invitationID}`, { responseType: 'blob' })
    const { data } = await defaultSuccess(response)
    setAccessControlQR(URL.createObjectURL(data))
  } catch (error) {
    defaultCatch(error, navigate, () => getAccessControlQR(invitationID, api, {
      defaultSuccess,
      defaultCatch,
      navigate,
      setAccessControlQR,
    }))
  }
}

const getAPIidentificationUrl = () => {
  const runtimeUrl = typeof window !== 'undefined'
    ? window.__RUNTIME_CONFIG__?.REACT_APP_IDENTIFICATION_DOCUMENT_API_URL
    : ''
  return runtimeUrl || process.env.REACT_APP_IDENTIFICATION_DOCUMENT_API_URL
}

async function preprocess(imageData) {
  const [modelWidth, modelHeight] = MODEL_INPUT_SHAPE.slice(2);
  const canvas = new OffscreenCanvas(modelWidth, modelHeight);
  const ctx = canvas.getContext('2d');
  const imageBitmap = await createImageBitmap(imageData);

  const scale = Math.min(modelWidth / imageBitmap.width, modelHeight / imageBitmap.height);
  const scaledWidth = imageBitmap.width * scale;
  const scaledHeight = imageBitmap.height * scale;
  const dx = (modelWidth - scaledWidth) / 2;
  const dy = (modelHeight - scaledHeight) / 2;
  ctx.drawImage(imageBitmap, 0, 0, imageBitmap.width, imageBitmap.height, dx, dy, scaledWidth, scaledHeight);

  const processedImageData = ctx.getImageData(0, 0, modelWidth, modelHeight);
  const { data } = processedImageData;
  const float32Data = new Float32Array(3 * modelWidth * modelHeight);
  const red = [], green = [], blue = [];
  for (let i = 0; i < data.length; i += 4) {
    red.push(data[i] / 255);
    green.push(data[i + 1] / 255);
    blue.push(data[i + 2] / 255);
  }
  float32Data.set([...red, ...green, ...blue]);
  const tensor = new ort.Tensor('float32', float32Data, MODEL_INPUT_SHAPE);
  return { tensor, scale, dx, dy };
}

async function postprocess(outputTensor, scale, dx, dy, classLabels = ['ID Document']) {
  const boxes = [];
  const data = outputTensor.data;
  const numDetections = outputTensor.dims[2];
  for (let i = 0; i < numDetections; i++) {
    let maxScore = 0;
    let classId = -1;
    for (let j = 0; j < classLabels.length; j++) {
      const score = data[(4 + j) * numDetections + i];
      if (score > maxScore) {
        maxScore = score;
        classId = j;
      }
    }
    if (maxScore > CONFIDENCE_THRESHOLD) {
      const x_center = data[0 * numDetections + i];
      const y_center = data[1 * numDetections + i];
      const width = data[2 * numDetections + i];
      const height = data[3 * numDetections + i];
      const x1 = ((x_center - width / 2) - dx) / scale;
      const y1 = ((y_center - height / 2) - dy) / scale;
      const x2 = ((x_center + width / 2) - dx) / scale;
      const y2 = ((y_center + height / 2) - dy) / scale;
      boxes.push({ x1, y1, x2, y2, score: maxScore, label: classLabels[classId] });
    }
  }
  return nonMaxSuppression(boxes, IOU_THRESHOLD);
}

async function preprocessExtraction(imageBitmap) {
  const [modelWidth, modelHeight] = MODEL_INPUT_SHAPE.slice(2); // Ej: [640, 640]

  // Usa OffscreenCanvas para el procesamiento en el worker
  const canvas = new OffscreenCanvas(modelWidth, modelHeight);
  const ctx = canvas.getContext('2d');

  // Dibuja la imagen recortada en el canvas, redimensionándola al tamaño del modelo
  ctx.drawImage(imageBitmap, 0, 0, modelWidth, modelHeight);

  const imageData = ctx.getImageData(0, 0, modelWidth, modelHeight);
  const { data } = imageData;
  const float32Data = new Float32Array(3 * modelWidth * modelHeight);

  const red = [], green = [], blue = [];
  for (let i = 0; i < data.length; i += 4) {
    red.push(data[i] / 255.0);
    green.push(data[i + 1] / 255.0);
    blue.push(data[i + 2] / 255.0);
  }
  float32Data.set([...red, ...green, ...blue]);

  return new ort.Tensor('float32', float32Data, MODEL_INPUT_SHAPE);
}

async function postprocessExtraction(
  outputTensor,
  originalWidth,
  originalHeight,
  classLabels,
  CONFIDENCE_THRESHOLD = 0.5,
  IOU_THRESHOLD = 0.5,
) {
  const boxes = [];
  const data = outputTensor.data;
  const outputShape = outputTensor.dims;
  const numDetections = outputShape[2];

  for (let i = 0; i < numDetections; i++) {
    let maxScore = 0;
    let classId = -1;
    
    // Encontrar la clase con la puntuación más alta Y su índice
    for (let j = 0; j < classLabels.length; j++) {
        const score = data[(4 + j) * numDetections + i];
        if (score > maxScore) {
            maxScore = score;
            classId = j;
        }
    }

    if (maxScore > CONFIDENCE_THRESHOLD) {
      const x_center = data[0 * numDetections + i];
      const y_center = data[1 * numDetections + i];
      const width = data[2 * numDetections + i];
      const height = data[3 * numDetections + i];

      const x1 = (x_center - width / 2) / MODEL_INPUT_SHAPE[2] * originalWidth;
      const y1 = (y_center - height / 2) / MODEL_INPUT_SHAPE[3] * originalHeight;
      const x2 = (x_center + width / 2) / MODEL_INPUT_SHAPE[2] * originalWidth;
      const y2 = (y_center + height / 2) / MODEL_INPUT_SHAPE[3] * originalHeight;

      boxes.push({
        x1, y1, x2, y2,
        score: maxScore,
        label: classLabels[classId]
      });
    }
  }

  return nonMaxSuppression(boxes, IOU_THRESHOLD);
}

function nonMaxSuppression(boxes, iouThreshold) {
  boxes.sort((a, b) => b.score - a.score);
  const result = [];
  while (boxes.length > 0) {
    const currentBox = boxes[0];
    result.push(currentBox);
    const remainingBoxes = [];
    for (const box of boxes) {
      if (box !== currentBox) {
        const iou = calculateIoU(currentBox, box);
        if (iou < iouThreshold) {
          remainingBoxes.push(box);
        }
      }
    }
    boxes = remainingBoxes;
  }
  return result;
}

function calculateIoU(box1, box2) {
  const x1 = Math.max(box1.x1, box2.x1);
  const y1 = Math.max(box1.y1, box2.y1);
  const x2 = Math.min(box1.x2, box2.x2);
  const y2 = Math.min(box1.y2, box2.y2);
  const intersectionArea = Math.max(0, x2 - x1) * Math.max(0, y2 - y1);
  const box1Area = (box1.x2 - box1.x1) * (box1.y2 - box1.y1);
  const box2Area = (box2.x2 - box2.x1) * (box2.y2 - box2.y1);
  const unionArea = box1Area + box2Area - intersectionArea;
  return intersectionArea / unionArea;
}

function isSharp(box, fullImageData) {
  if (!box) return [false, 0];
  const boxWidth = Math.floor(box.x2 - box.x1);
  const boxHeight = Math.floor(box.y2 - box.y1);
  if (boxWidth <= 0 || boxHeight <= 0) return [false, 0];
  const gray = [];
  for (let y = 0; y < boxHeight; y++) {
    for (let x = 0; x < boxWidth; x++) {
      const sourceX = Math.floor(box.x1) + x;
      const sourceY = Math.floor(box.y1) + y;
      const i = (sourceY * fullImageData.width + sourceX) * 4;
      const r = fullImageData.data[i];
      const g = fullImageData.data[i + 1];
      const b = fullImageData.data[i + 2];
      gray.push(0.299 * r + 0.587 * g + 0.114 * b);
    }
  }
  const laplacian = [];
  for (let y = 1; y < boxHeight - 1; y++) {
    for (let x = 1; x < boxWidth - 1; x++) {
      const idx = y * boxWidth + x;
      const lap = -gray[idx - boxWidth] - gray[idx - 1] + 4 * gray[idx] - gray[idx + 1] - gray[idx + boxWidth];
      laplacian.push(lap);
    }
  }
  if (laplacian.length === 0) return [false, 0];
  const mean = laplacian.reduce((a, b) => a + b, 0) / laplacian.length;
  const variance = laplacian.reduce((a, b) => a + (b - mean) ** 2, 0) / laplacian.length;
  return [variance > SHARPNESS_THRESHOLD, variance];
}

const hasCorrectAspectRatio = (box) => {
  const width = box.x2 - box.x1;
  const height = box.y2 - box.y1;
  if (height === 0) return [false, 0];
  const aspectRatio = width / height;
  return [aspectRatio >= MIN_ASPECT_RATIO, aspectRatio];
};

const isCloseEnough = (box, canvas) => {
  const boxArea = (box.x2 - box.x1) * (box.y2 - box.y1);
  const canvasArea = canvas.width * canvas.height;
  const area = (boxArea / canvasArea);
  return [area >= MIN_AREA_RATIO, area];
};

const createCanvas = (width, height) => new OffscreenCanvas(width, height);

const isTodayFromJsDate = date => {
  const today = DateTime.local()
  const _date = DateTime.fromJSDate(date)
  return today.hasSame(_date, 'day')
} // true or false

const getCurrentHour = (plusHour = 0, plusMinutes = 0) => {
  return DateTime.now().plus({
    hour: plusHour,
    minutes: plusMinutes,
  }).toJSDate()
}

const getDefaultEntryTime = (date, defaultValue) => {
  const isTodayValue = isTodayFromJsDate(date)

  if (defaultValue && !isTodayValue) {
    return defaultValue
  }

  if (isTodayValue) {
    const now = getCurrentHour(0, 10)
    return defaultValue && defaultValue >= now
      ? defaultValue
      : now
  }

  return defaultValue || new Date().setHours(8, 0, 0, 0)
}

const stringHourToJsDate = stringHour => {
  if (!stringHour) return new Date()
  const [hour, minute] = stringHour.split(':')
  const _hour = { hour: Number(hour), minute: Number(minute), second: 0 }
  return DateTime.local().set(_hour).toJSDate() // Js Date 
}

const getWorkplaceDefaultEntryTime = state => {
  return state.profile.workplace_default_entry_time ?
    stringHourToJsDate(state.profile.workplace_default_entry_time) :
    EIGHT_AM
}

const getWorkplaceDefaultExitTime = state => {
  return state.profile.workplace_default_exit_time ?
    stringHourToJsDate(state.profile.workplace_default_exit_time) :
    FIVE_PM
}

export {
  createCanvas,
  hasCorrectAspectRatio,
  isCloseEnough,
  isSharp,
  preprocess,
  preprocessExtraction,
  postprocess,
  postprocessExtraction,
  nonMaxSuppression,
  calculateIoU,
  getAPIidentificationUrl,
  isHome,
  getCompany,
  defaultFunction,
  getNumberToWeekDay,
  getCurrentBrowser,
  getAmenitieImage,
  getFullName,
  getFullNameGuest,
  getNormalizedEmailList,
  isValidEmailList,
  getWorkplaceReasons,
  getNoRowOverlay,
  getNoRowOverlayInvitations,
  getNoResultsOverlayInvitations,
  CustomPickersDay,
  ParsoSwitch,
  getVisitDestination,
  stopEvent,
  sortItems,
  getPlace,
  debounce,
  getTime,
  downloadQRAsImage,
  getAccessControlQR,
  getDefaultEntryTime,
  getWorkplaceDefaultEntryTime,
  getWorkplaceDefaultExitTime,
  isTodayFromJsDate,
}
